import { ExpandLess, ExpandMore } from '@tunasong/icons'
import { getEntityIcon, usePlugins } from '@tunasong/plugin-lib'
import type { Entity, Persisted } from '@tunasong/schemas'
import {Typography, usePageTurner, Button, Menu, ListItemIcon, MenuItem, ListItemText, ButtonGroup, type ButtonGroupProps} from '@tunasong/ui-lib'
import type { FC } from 'react'
import React, { useMemo, useState } from 'react'
import styles from './performance-nav.module.css'

export interface PerformanceNavigationProps extends Omit<ButtonGroupProps, 'onChange'> {
  className?: string
  entities: Persisted<Entity>[]
  view: HTMLElement | null
  current?: Persisted<Entity>
  onChange(e: Persisted<Entity>): void
}

export const PerformanceNavigation: FC<PerformanceNavigationProps> = props => {
  const { entities, current, view, onChange, ...boxProps } = props
  const [menuRef, setMenuRef] = useState<HTMLElement | null>(null)
  const plugins = usePlugins('performance')
  const [expectIdx, setExpectIdx] = useState(-1)
  const currentIdx = useMemo(() => {
    if (expectIdx >= 0 && entities[expectIdx]?.id === current?.id) {
      return expectIdx
    }
    return current ? entities.findIndex(e => e.id === current.id) : 0
  }, [current, entities, expectIdx])

  const handleShowMenu = (e: React.MouseEvent<HTMLElement>) => setMenuRef(e.currentTarget)
  const handleCloseMenu = () => setMenuRef(null)
  const handleOpen = (entity: Persisted<Entity>, expectedIndex: number) => () => {
    handleCloseMenu()
    setExpectIdx(expectedIndex)
    onChange(entity)
  }
  const previous = entities[currentIdx - 1]
  const next = entities[currentIdx + 1]

  usePageTurner({
    container: view,
    onNext: () => next && handleOpen(next, currentIdx + 1)(),
    onPrevious: () => previous && handleOpen(previous, currentIdx - 1)(),
  })

  return (
    <>
      <ButtonGroup
        {...boxProps}
        style={boxProps?.style}
        className={[styles.buttonGroup, boxProps?.className, 'tuna-hide-print'].filter(Boolean).join(' ')}
        variant="contained"
        aria-label="Performance Navigation"
      >
        <Button
          variant="contained"
          onClick={handleShowMenu}
          disabled={entities.length <= 1}
          size="large"
          aria-haspopup="menu"
          aria-label={`${currentIdx + 1} / ${entities.length}, jump to`}
          className={[styles.menuButton, styles.stageTarget].join(' ')}
        >
          <Typography
            variant="body1"
            component="div"
            className={styles.buttonText}
          >
            {currentIdx + 1} / {entities.length}
          </Typography>
        </Button>
        <Button
          variant="contained"
          size="large"
          className={[styles.navButton, styles.stageTarget].join(' ')}
          title={previous?.name}
          aria-label={`Previous: ${previous?.name ?? 'Start'}`}
          disabled={!previous}
          endIcon={<ExpandLess />}
          onClick={handleOpen(previous, currentIdx - 1)}
        >
          <Typography
            variant="body1"
            component="div"
            className={styles.buttonText}
          >
            {previous?.name ?? 'Start'}
          </Typography>
        </Button>
        <Button
          variant="contained"
          size="large"
          className={[styles.navButton, styles.stageTarget].join(' ')}
          endIcon={<ExpandMore />}
          title={next?.name}
          aria-label={`Next: ${next?.name ?? 'End'}`}
          disabled={!next}
          onClick={handleOpen(next, currentIdx + 1)}
        >
          <Typography
            variant="body1"
            component="div"
            className={styles.buttonText}
          >
            {next?.name ?? 'End'}
          </Typography>
        </Button>
      </ButtonGroup>
      <Menu open={Boolean(menuRef)} anchorEl={menuRef} onClose={handleCloseMenu}>
        {entities.map((e, idx) => {
          const Icon = getEntityIcon(e, plugins)
          return (
            <MenuItem
              disabled={idx === currentIdx}
              aria-current={idx === currentIdx ? 'true' : undefined}
              key={idx}
              onClick={handleOpen(e, idx)}
              className={styles.menuItem}
            >
              <ListItemIcon>
                <Icon />
              </ListItemIcon>
              <ListItemText>{e.name}</ListItemText>
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}

export default PerformanceNavigation
